import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";
const ADMIN_TOKEN_KEY = "oryyn_admin_token";

function Logo({ size = 34 }) {
  return (
    <div
      className="admin-logo"
      style={{
        fontSize: size,
        fontWeight: 800,
        letterSpacing: "-0.045em",
        background: "linear-gradient(118deg, #0d9488 0%, #14b8a6 42%, #fb7185 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
      }}
    >
      oryyn
    </div>
  );
}

function getAuthHeaders() {
  const token = sessionStorage.getItem(ADMIN_TOKEN_KEY) || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatNumber(value) {
  return new Intl.NumberFormat().format(Number(value || 0));
}

function timeAgo(dateValue) {
  if (!dateValue) return "just now";
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(dateValue).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function truncate(text, length = 40) {
  if (!text) return "";
  return text.length > length ? `${text.slice(0, length)}...` : text;
}

function AnimatedNumber({ value, suffix = "" }) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const target = Number(value || 0);
    const duration = 700;
    const started = performance.now();
    let frame;
    function tick(now) {
      const progress = Math.min((now - started) / duration, 1);
      setDisplayValue(Math.round(target * progress));
      if (progress < 1) frame = requestAnimationFrame(tick);
    }
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return <>{formatNumber(displayValue)}{suffix}</>;
}

function SkeletonCard() {
  return (
    <div className="metric-card skeleton-card">
      <div className="skeleton skeleton-icon" />
      <div className="skeleton skeleton-value" />
      <div className="skeleton skeleton-label" />
    </div>
  );
}

function MetricCard({ icon, value, label, accent, suffix = "", loading }) {
  if (loading) return <SkeletonCard />;
  return (
    <article className="metric-card" style={{ "--accent": accent }}>
      <div className="metric-icon">{icon}</div>
      <strong><AnimatedNumber value={value} suffix={suffix} /></strong>
      <span>{label}</span>
    </article>
  );
}

function LoginGate({ password, setPassword, onLogin, error, loading }) {
  return (
    <main className="admin-login-page">
      <section className="admin-login-card">
        <Logo size={56} />
        <p>Admin Dashboard</p>
        {error && <div className="admin-error">{error}</div>}
        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter") onLogin(); }}
          placeholder="Admin password"
          autoFocus
        />
        <button onClick={onLogin} type="button" disabled={loading}>{loading ? "Signing in…" : "Login"}</button>
      </section>
    </main>
  );
}

function RecentSearches({ searches, limit, onLimitChange }) {
  const [input, setInput] = useState(String(limit));

  function handleApply() {
    const val = Math.min(200, Math.max(1, parseInt(input, 10) || 20));
    setInput(String(val));
    onLimitChange(val);
  }

  return (
    <section className="admin-panel recent-panel">
      <div className="panel-heading" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2>Recent Searches</h2>
          <p>Last {searches.length} queries</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="number"
            min={1}
            max={200}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleApply()}
            style={{ width: 64, height: 32, padding: "0 8px", border: "1px solid #dadce0", borderRadius: 8, fontSize: 13, fontFamily: "Nunito, sans-serif", textAlign: "center" }}
          />
          <button onClick={handleApply} type="button" style={{ height: 32, padding: "0 12px", fontSize: 12 }}>Show</button>
        </div>
      </div>
      {searches.length === 0 ? (
        <div className="empty-state">No searches yet</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Query</th>
                <th>Status</th>
                <th>Response</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {searches.map((search, index) => (
                <tr key={`${search.query}-${search.created_at}-${index}`}>
                  <td title={search.query}>{truncate(search.query)}</td>
                  <td>{search.success ? "✅" : "❌"}</td>
                  <td>{search.response_time_ms || 0}ms</td>
                  <td>{timeAgo(search.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function rankClass(index) {
  if (index === 0) return "gold";
  if (index === 1) return "silver";
  if (index === 2) return "bronze";
  return "";
}

function PopularQueries({ queries }) {
  return (
    <section className="admin-panel popular-panel">
      <div className="panel-heading">
        <h2>Trending Queries</h2>
        <p>Top searched topics</p>
      </div>
      {queries.length === 0 ? (
        <div className="empty-state">Not enough data yet</div>
      ) : (
        <div className="popular-list">
          {queries.map((item, index) => (
            <article key={`${item.query}-${index}`} className="popular-item">
              <span className={`rank ${rankClass(index)}`}>#{index + 1}</span>
              <strong title={item.query}>{truncate(item.query, 30)}</strong>
              <em>{item.count}</em>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function StatusIndicator({ online, title, label }) {
  return (
    <div className="status-indicator">
      <span className={`status-dot ${online ? "online" : "offline"}`} />
      <div>
        <strong>{title}</strong>
        <p>{label}</p>
      </div>
    </div>
  );
}

function SystemHealth({ health, databaseConnected, onRefresh, lastHealthCheck }) {
  return (
    <section className="admin-panel health-panel">
      <div className="panel-heading health-heading">
        <div>
          <h2>System Health</h2>
          <p>Last checked {lastHealthCheck || "not yet"}</p>
        </div>
        <button onClick={onRefresh} type="button">Refresh health</button>
      </div>
      <div className="health-grid">
        <StatusIndicator online={health.ollama === "online"} title="Groq API" label={health.llm_model || "LLM inference"} />
        <StatusIndicator online={health.searxng === "online"} title="SearXNG" label="Web Search Engine" />
        <StatusIndicator
          online={databaseConnected}
          title={databaseConnected ? "Connected" : "Disconnected"}
          label="Supabase Database"
        />
      </div>
    </section>
  );
}

function AnnouncementComposer() {
  const [message, setMessage] = useState("");
  const [level, setLevel] = useState("info");
  const [link, setLink] = useState("");
  const [current, setCurrent] = useState(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const loadCurrent = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/announcement`, {
        headers: getAuthHeaders(),
      });
      setCurrent(res.data?.announcement || null);
    } catch {
      setCurrent(null);
    }
  }, []);

  useEffect(() => {
    loadCurrent();
  }, [loadCurrent]);

  const publish = async () => {
    if (!message.trim()) {
      setStatus("Message cannot be empty.");
      return;
    }
    setBusy(true);
    setStatus("");
    try {
      await axios.post(
        `${API_URL}/admin/announcement`,
        { message: message.trim(), level, link: link.trim() },
        { headers: getAuthHeaders() },
      );
      setStatus("Published — live for all visitors.");
      setMessage("");
      setLink("");
      await loadCurrent();
    } catch (e) {
      setStatus(e.response?.data?.detail || "Failed to publish.");
    }
    setBusy(false);
  };

  const clear = async () => {
    setBusy(true);
    setStatus("");
    try {
      await axios.delete(`${API_URL}/admin/announcement`, {
        headers: getAuthHeaders(),
      });
      setStatus("Cleared — banner removed.");
      await loadCurrent();
    } catch {
      setStatus("Failed to clear.");
    }
    setBusy(false);
  };

  return (
    <section className="admin-panel">
      <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>📣 Announcement banner</h3>
      <p style={{ fontSize: 12, color: "#5f6368", margin: "0 0 12px" }}>
        Shows a site-wide bar to every visitor until you clear it.
      </p>

      {current && (
        <div style={{ fontSize: 12.5, background: "#eff6ff", border: "1px solid #cde0ff", borderRadius: 8, padding: "8px 10px", marginBottom: 12 }}>
          <strong>Live now ({current.level}):</strong> {current.message}
        </div>
      )}

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="e.g. New: Videos tab is now live! 🎉"
        maxLength={280}
        rows={2}
        style={{ width: "100%", padding: 10, fontSize: 13, borderRadius: 8, border: "1px solid #dfe1e5", resize: "vertical" }}
      />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
        <select value={level} onChange={(e) => setLevel(e.target.value)} style={{ padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "1px solid #dfe1e5" }}>
          <option value="info">Info (blue)</option>
          <option value="success">Success (green)</option>
          <option value="warning">Warning (amber)</option>
        </select>
        <input
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="Optional link (https://…)"
          style={{ flex: 1, minWidth: 160, padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "1px solid #dfe1e5" }}
        />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
        <button onClick={publish} disabled={busy} type="button" style={{ padding: "8px 16px", fontSize: 13, fontWeight: 700 }}>
          {busy ? "…" : "Publish"}
        </button>
        {current && (
          <button onClick={clear} disabled={busy} type="button" style={{ padding: "8px 16px", fontSize: 13, background: "#fce8e6", color: "#c5221f" }}>
            Clear banner
          </button>
        )}
        {status && <span style={{ fontSize: 12, color: "#5f6368" }}>{status}</span>}
      </div>
    </section>
  );
}

function PushComposer() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [info, setInfo] = useState({ configured: false, subscribers: 0 });
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/push/status`, {
        headers: getAuthHeaders(),
      });
      setInfo(res.data || { configured: false, subscribers: 0 });
    } catch {
      setInfo({ configured: false, subscribers: 0 });
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      setStatus("Title and message are required.");
      return;
    }
    setBusy(true);
    setStatus("");
    try {
      const res = await axios.post(
        `${API_URL}/admin/push/send`,
        { title: title.trim(), body: body.trim(), url: url.trim() || "/" },
        { headers: getAuthHeaders() },
      );
      const r = res.data || {};
      setStatus(`Sent to ${r.sent || 0} device(s)${r.removed ? `, pruned ${r.removed} expired` : ""}.`);
      setTitle("");
      setBody("");
      await loadStatus();
    } catch (e) {
      setStatus(e.response?.data?.detail || "Failed to send.");
    }
    setBusy(false);
  };

  return (
    <section className="admin-panel">
      <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>🔔 Push notification</h3>
      <p style={{ fontSize: 12, color: "#5f6368", margin: "0 0 12px" }}>
        {info.configured
          ? `Sends a browser push to ${info.subscribers} subscribed device(s) — even when the site is closed.`
          : "⚠️ Push not configured on the server (VAPID keys missing)."}
      </p>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title — e.g. New on Oryyn"
        maxLength={80}
        style={{ width: "100%", padding: 10, fontSize: 13, borderRadius: 8, border: "1px solid #dfe1e5", marginBottom: 8 }}
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Message — e.g. The Videos tab just launched 🎬"
        maxLength={180}
        rows={2}
        style={{ width: "100%", padding: 10, fontSize: 13, borderRadius: 8, border: "1px solid #dfe1e5", resize: "vertical" }}
      />
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Open URL on click (default /)"
        style={{ width: "100%", padding: 10, fontSize: 13, borderRadius: 8, border: "1px solid #dfe1e5", marginTop: 8 }}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
        <button onClick={send} disabled={busy || !info.configured} type="button" style={{ padding: "8px 16px", fontSize: 13, fontWeight: 700 }}>
          {busy ? "Sending…" : "Send push"}
        </button>
        {status && <span style={{ fontSize: 12, color: "#5f6368" }}>{status}</span>}
      </div>
    </section>
  );
}

function EmailComposer() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [testTo, setTestTo] = useState("");
  const [info, setInfo] = useState({ configured: false, recipients: 0 });
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/email/status`, {
        headers: getAuthHeaders(),
      });
      setInfo(res.data || { configured: false, recipients: 0 });
    } catch {
      setInfo({ configured: false, recipients: 0 });
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const doSend = async (asTest) => {
    if (!subject.trim() || !body.trim()) {
      setStatus("Subject and body are required.");
      return;
    }
    if (asTest && !testTo.trim()) {
      setStatus("Enter a test email address.");
      return;
    }
    if (!asTest && !window.confirm(`Send this email to all ${info.recipients} users?`)) {
      return;
    }
    setBusy(true);
    setStatus("");
    try {
      const payload = { subject: subject.trim(), body: body.trim() };
      if (asTest) payload.test_to = testTo.trim();
      const res = await axios.post(`${API_URL}/admin/email/send`, payload, {
        headers: getAuthHeaders(),
      });
      const r = res.data || {};
      setStatus(`${asTest ? "Test sent" : "Sent"}: ${r.sent || 0} delivered${r.failed ? `, ${r.failed} failed` : ""}.${r.error ? ` (${r.error})` : ""}`);
      if (!asTest) {
        setSubject("");
        setBody("");
      }
    } catch (e) {
      setStatus(e.response?.data?.detail || "Failed to send.");
    }
    setBusy(false);
  };

  return (
    <section className="admin-panel">
      <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>✉️ Email blast</h3>
      <p style={{ fontSize: 12, color: "#5f6368", margin: "0 0 12px" }}>
        {info.configured
          ? `Emails your ${info.recipients} registered user(s) via Resend.`
          : "⚠️ Email not configured (RESEND_API_KEY missing on server)."}
      </p>

      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject"
        maxLength={120}
        style={{ width: "100%", padding: 10, fontSize: 13, borderRadius: 8, border: "1px solid #dfe1e5", marginBottom: 8 }}
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your message… (plain text; line breaks preserved)"
        rows={5}
        style={{ width: "100%", padding: 10, fontSize: 13, borderRadius: 8, border: "1px solid #dfe1e5", resize: "vertical" }}
      />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
        <input
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          placeholder="test@email.com"
          style={{ flex: 1, minWidth: 160, padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "1px solid #dfe1e5" }}
        />
        <button onClick={() => doSend(true)} disabled={busy || !info.configured} type="button" style={{ padding: "8px 14px", fontSize: 13, fontWeight: 600, background: "#f1f3f4", color: "#202124" }}>
          Send test
        </button>
        <button onClick={() => doSend(false)} disabled={busy || !info.configured} type="button" style={{ padding: "8px 16px", fontSize: 13, fontWeight: 700 }}>
          {busy ? "Sending…" : `Send to all (${info.recipients})`}
        </button>
      </div>
      {status && <p style={{ fontSize: 12, color: "#5f6368", marginTop: 8 }}>{status}</p>}
    </section>
  );
}

function UsersPanel({ users, usersLoading, onBan, onUnban, actionLoading }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const filtered = useMemo(() => {
    return users.filter((u) => {
      const matchesSearch =
        !search ||
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        (u.full_name || "").toLowerCase().includes(search.toLowerCase());
      const matchesFilter =
        filter === "all" ||
        (filter === "pro" && u.is_pro) ||
        (filter === "free" && !u.is_pro) ||
        (filter === "banned" && u.is_banned);
      return matchesSearch && matchesFilter;
    });
  }, [users, search, filter]);

  return (
    <section className="admin-panel users-panel">
      <div className="panel-heading">
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <h2>User Management</h2>
          <span style={{ fontSize: 13, color: "#5f6368", fontWeight: 700 }}>{users.length} total</span>
        </div>
        <p>Search, ban, or manage all registered users</p>
      </div>

      <div className="users-toolbar">
        <input
          type="search"
          placeholder="Search by email or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="users-search-input"
        />
        <div className="users-filter-group">
          {["all", "pro", "free", "banned"].map((f) => (
            <button
              key={f}
              className={`filter-chip ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
              type="button"
            >
              {f === "all" ? "All" : f === "pro" ? "⭐ Pro" : f === "free" ? "Free" : "🚫 Banned"}
            </button>
          ))}
        </div>
      </div>

      {usersLoading ? (
        <div className="empty-state">Loading users...</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">No users match the filter</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Joined</th>
                <th>Last seen</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} style={{ opacity: u.is_banned ? 0.6 : 1 }}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: "50%",
                        background: u.is_pro ? "#FFF3CD" : "#e8f0fe",
                        color: u.is_pro ? "#856404" : "#0d9488",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, fontWeight: 800, flexShrink: 0,
                      }}>
                        {(u.full_name || u.email)[0].toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>
                        {truncate(u.full_name || "—", 20)}
                      </span>
                    </div>
                  </td>
                  <td title={u.email} style={{ fontSize: 12, color: "#5f6368" }}>{truncate(u.email, 28)}</td>
                  <td>
                    {u.is_pro ? (
                      <span style={{ background: "#FFF3CD", color: "#856404", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 800, border: "1px solid #FFD700" }}>
                        ⭐ Pro
                      </span>
                    ) : (
                      <span style={{ background: "#f1f3f4", color: "#5f6368", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                        Free
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: "#5f6368" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td style={{ fontSize: 12, color: "#5f6368" }}>{timeAgo(u.last_login)}</td>
                  <td>
                    {u.is_banned ? (
                      <span style={{ color: "#EA4335", fontSize: 12, fontWeight: 700 }}>🚫 Banned</span>
                    ) : (
                      <span style={{ color: "#34A853", fontSize: 12, fontWeight: 700 }}>✓ Active</span>
                    )}
                  </td>
                  <td>
                    {u.is_banned ? (
                      <button
                        className="action-button unban"
                        onClick={() => onUnban(u.id)}
                        disabled={actionLoading === u.id}
                        type="button"
                      >
                        {actionLoading === u.id ? "..." : "Unban"}
                      </button>
                    ) : (
                      <button
                        className="action-button ban"
                        onClick={() => onBan(u.id)}
                        disabled={actionLoading === u.id}
                        type="button"
                      >
                        {actionLoading === u.id ? "..." : "Ban"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(() => Boolean(sessionStorage.getItem(ADMIN_TOKEN_KEY)));
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [recentLimit, setRecentLimit] = useState(20);
  const [statsError, setStatsError] = useState("");
  const [healthError, setHealthError] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [lastHealthCheck, setLastHealthCheck] = useState("");
  const [now, setNow] = useState(Date.now());

  const fetchStats = useCallback(async (limit = 20) => {
    setLoading(true);
    setStatsError("");
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, { headers: getAuthHeaders(), params: { recent_limit: limit } });
      setStats(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      if (error.response?.status === 401) {
        // Token missing/expired — return to the login gate
        sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        setAuthenticated(false);
      } else {
        setStatsError("Backend offline. Start the backend server.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHealth = useCallback(async () => {
    setHealthError("");
    try {
      const response = await axios.get(`${API_URL}/admin/health`, { headers: getAuthHeaders() });
      setHealth(response.data);
      setLastHealthCheck(new Date().toLocaleTimeString());
    } catch (error) {
      if (error.response?.status !== 401) {
        setHealthError("Backend offline.");
      }
      setHealth({});
      setLastHealthCheck(new Date().toLocaleTimeString());
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/users`, { headers: getAuthHeaders() });
      setUsers(response.data.users || []);
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authenticated) return undefined;
    fetchStats(recentLimit);
    fetchHealth();
    fetchUsers();
    const statsTimer = setInterval(() => fetchStats(recentLimit), 30000);
    const clockTimer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(statsTimer);
      clearInterval(clockTimer);
    };
  }, [authenticated, fetchHealth, fetchStats, fetchUsers, recentLimit]);

  const handleBan = useCallback(async (userId) => {
    if (!window.confirm("Ban this user? They will be unable to log in.")) return;
    setActionLoading(userId);
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/ban`, {}, { headers: getAuthHeaders() });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, is_banned: true } : u));
    } catch {
      alert("Failed to ban user. Check backend connection.");
    } finally {
      setActionLoading(null);
    }
  }, []);

  const handleUnban = useCallback(async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/unban`, {}, { headers: getAuthHeaders() });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, is_banned: false } : u));
    } catch {
      alert("Failed to unban user.");
    } finally {
      setActionLoading(null);
    }
  }, []);

  const secondsAgo = lastRefresh ? Math.max(0, Math.floor((now - lastRefresh.getTime()) / 1000)) : null;
  const countdown = secondsAgo === null ? 30 : Math.max(0, 30 - (secondsAgo % 30));

  const recentSearches = stats?.recent_searches || [];
  const popularQueries = stats?.popular_queries || [];
  const totalSearches = stats?.total_searches || 0;
  const successfulCount = stats?.successful_searches ?? recentSearches.filter((s) => s.success).length;
  const successDenominator = stats?.successful_searches !== undefined ? totalSearches : recentSearches.length;
  const successRate = successDenominator > 0 ? Math.round((successfulCount / successDenominator) * 100) : 0;
  const proUsers = stats?.pro_users || 0;
  const revenue = (proUsers * 9.99).toFixed(2);

  const metricCards = useMemo(() => [
    { icon: "🔍", value: totalSearches, label: "Total Searches", accent: "#0d9488" },
    { icon: "📈", value: stats?.searches_today || 0, label: "Today", accent: "#34A853" },
    { icon: "👥", value: stats?.total_users || 0, label: "Total Users", accent: "#f59e0b" },
    { icon: "⭐", value: proUsers, label: "Pro Users", accent: "#fb7185" },
    { icon: "✅", value: successRate, label: "Success Rate", accent: "#9C27B0", suffix: "%" },
  ], [stats, successRate, totalSearches, proUsers]);

  async function handleLogin() {
    if (!password || loggingIn) return;
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API_URL}/admin/login`, { password });
      sessionStorage.setItem(ADMIN_TOKEN_KEY, res.data.token);
      setAuthenticated(true);
      setPassword("");
    } catch (err) {
      setLoginError(err.response?.status === 401 ? "Incorrect password" : "Could not reach the server");
    }
    setLoggingIn(false);
  }

  function handleLogout() {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthenticated(false);
  }

  if (!authenticated) {
    return (
      <>
        <DashboardStyles />
        <LoginGate password={password} setPassword={setPassword} onLogin={handleLogin} error={loginError} loading={loggingIn} />
      </>
    );
  }

  return (
    <>
      <DashboardStyles />
      <div className="admin-shell">
        <header className="admin-navbar">
          <div className="admin-nav-left">
            <Logo size={28} />
            <span>Admin</span>
          </div>
          <div className="admin-nav-right">
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "#5f6368" }}>
                Last updated: {secondsAgo === null ? "never" : `${secondsAgo}s ago`}
              </div>
              <div style={{ fontSize: 11, color: "#9aa0a6" }}>Next in {countdown}s</div>
            </div>
            {revenue !== "0.00" && (
              <div style={{ fontSize: 13, fontWeight: 800, color: "#34A853", background: "#e6f4ea", padding: "4px 12px", borderRadius: 8 }}>
                ~${revenue}/mo MRR
              </div>
            )}
            <button onClick={() => { fetchStats(recentLimit); fetchHealth(); fetchUsers(); }} type="button">Refresh</button>
            <a href="/">Back to Oryyn</a>
            <button className="logout-button" onClick={handleLogout} type="button">Logout</button>
          </div>
        </header>

        <main className="admin-main">
          {(statsError || healthError) && (
            <div className="admin-alert">{statsError || healthError}</div>
          )}

          <section className="stats-grid">
            {metricCards.map((card) => (
              <MetricCard key={card.label} loading={loading && !stats} {...card} />
            ))}
          </section>

          <section className="admin-two-column">
            <RecentSearches searches={recentSearches} limit={recentLimit} onLimitChange={(val) => { setRecentLimit(val); fetchStats(val); }} />
            <PopularQueries queries={popularQueries} />
          </section>

          <div className="admin-two-column">
            <AnnouncementComposer />
            <PushComposer />
          </div>

          <EmailComposer />

          <UsersPanel
            users={users}
            usersLoading={usersLoading}
            onBan={handleBan}
            onUnban={handleUnban}
            actionLoading={actionLoading}
          />

          <SystemHealth
            health={health}
            databaseConnected={Boolean(stats) && !statsError}
            onRefresh={fetchHealth}
            lastHealthCheck={lastHealthCheck}
          />
        </main>
      </div>
    </>
  );
}

function DashboardStyles() {
  return (
    <style>{`
      .admin-logo {
        font-family: Nunito, sans-serif;
        font-weight: 800;
        letter-spacing: 0;
        line-height: 1;
        white-space: nowrap;
      }
      .admin-logo span {
        display: inline-block;
        transition: transform 160ms ease;
      }
      .admin-logo:hover span { transform: scale(1.08); }

      .admin-login-page {
        min-height: 100vh;
        display: grid;
        place-items: center;
        padding: 24px;
        background: #f8f9fa;
        font-family: Nunito, sans-serif;
      }
      .admin-login-card {
        width: 100%;
        max-width: 390px;
        padding: 32px;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 12px;
        box-shadow: 0 1px 6px rgba(32,33,36,0.1);
        text-align: center;
      }
      .admin-login-card p { margin: 12px 0 24px; color: #5f6368; font-size: 16px; font-weight: 700; }
      .admin-login-card input {
        width: 100%;
        height: 46px;
        margin-bottom: 12px;
        padding: 0 14px;
        border: 1px solid #dadce0;
        border-radius: 8px;
        font-size: 15px;
        box-sizing: border-box;
      }
      .admin-login-card button,
      .admin-navbar button,
      .health-heading button {
        min-height: 38px;
        padding: 0 16px;
        border: 0;
        border-radius: 8px;
        background: #4285f4;
        color: #fff;
        font-weight: 800;
        cursor: pointer;
        font-family: Nunito, sans-serif;
      }
      .admin-error, .admin-alert {
        padding: 12px 14px;
        background: #fce8e6;
        border: 1px solid #fad2cf;
        border-radius: 8px;
        color: #c5221f;
        font-size: 13px;
        font-weight: 700;
      }
      .admin-error { margin-bottom: 14px; }
      .admin-alert { margin-bottom: 18px; }

      .admin-shell {
        min-height: 100vh;
        background: #f8f9fa;
        color: #202124;
        font-family: Nunito, sans-serif;
      }
      .admin-navbar {
        position: fixed;
        z-index: 40;
        top: 0; left: 0; right: 0;
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 0 24px;
        background: #fff;
        border-bottom: 1px solid #e0e0e0;
        box-shadow: 0 1px 6px rgba(32,33,36,0.08);
      }
      .admin-nav-left, .admin-nav-right {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .admin-nav-left span {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        padding: 0 9px;
        border-radius: 999px;
        background: #e8f0fe;
        color: #0d9488;
        font-size: 12px;
        font-weight: 800;
      }
      .admin-nav-right a { color: #0d9488; font-size: 13px; font-weight: 800; text-decoration: none; }
      .admin-navbar .logout-button { background: #fce8e6; color: #c5221f; }

      .admin-main {
        width: min(1280px, calc(100% - 48px));
        margin: 0 auto;
        padding: 92px 0 48px;
      }

      .stats-grid {
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 14px;
        margin-bottom: 18px;
      }
      .metric-card, .admin-panel {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 12px;
        box-shadow: 0 1px 6px rgba(32,33,36,0.1);
      }
      .metric-card {
        position: relative;
        min-height: 154px;
        padding: 22px;
        overflow: hidden;
        transition: transform 160ms ease, box-shadow 160ms ease;
      }
      .metric-card:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(32,33,36,0.13); }
      .metric-card::before {
        content: "";
        position: absolute;
        inset: 0 auto 0 0;
        width: 4px;
        background: var(--accent, #4285f4);
      }
      .metric-icon {
        width: 40px; height: 40px;
        display: grid; place-items: center;
        margin-bottom: 14px;
        border-radius: 50%;
        background: color-mix(in srgb, var(--accent, #4285f4) 14%, white);
        font-size: 18px;
      }
      .metric-card strong { display: block; color: #202124; font-size: 32px; line-height: 1; font-weight: 800; }
      .metric-card span { display: block; margin-top: 8px; color: #5f6368; font-size: 13px; font-weight: 800; }
      .skeleton-card::before { background: #e8eaed; }
      .skeleton { position: relative; overflow: hidden; border-radius: 8px; background: #eef0f2; }
      .skeleton::after {
        content: "";
        position: absolute; inset: 0;
        transform: translateX(-100%);
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.75), transparent);
        animation: shimmer 1.1s infinite;
      }
      .skeleton-icon { width: 40px; height: 40px; border-radius: 50%; margin-bottom: 14px; }
      .skeleton-value { width: 62%; height: 32px; margin-bottom: 10px; }
      .skeleton-label { width: 44%; height: 14px; }
      @keyframes shimmer { to { transform: translateX(100%); } }

      .admin-two-column {
        display: grid;
        grid-template-columns: 3fr 2fr;
        gap: 16px;
        margin-bottom: 18px;
      }
      .admin-panel { padding: 22px; margin-bottom: 18px; }
      .admin-panel:last-child { margin-bottom: 0; }

      .panel-heading { margin-bottom: 16px; }
      .panel-heading h2 { color: #202124; font-size: 18px; font-weight: 800; }
      .panel-heading p { margin-top: 3px; color: #70757a; font-size: 13px; }

      .table-wrap { overflow-x: auto; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; }
      th { padding: 12px; background: #f8f9fa; color: #5f6368; text-align: left; font-weight: 800; white-space: nowrap; }
      td { padding: 10px 12px; border-top: 1px solid #f1f3f4; color: #3c4043; }
      tbody tr:nth-child(even) { background: #fbfbfc; }
      tbody tr:hover { background: #e8f0fe; }
      .empty-state { display: grid; place-items: center; min-height: 120px; color: #9aa0a6; font-size: 14px; font-weight: 700; }

      .popular-list { display: grid; gap: 10px; }
      .popular-item {
        display: grid;
        grid-template-columns: 44px 1fr auto;
        align-items: center;
        gap: 10px;
        padding: 11px 12px;
        border: 1px solid #f1f3f4;
        border-radius: 10px;
        background: #fff;
      }
      .popular-item strong { overflow: hidden; color: #202124; font-size: 13px; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
      .popular-item em { min-width: 34px; padding: 4px 8px; border-radius: 999px; background: #f1f3f4; color: #5f6368; font-size: 12px; font-style: normal; font-weight: 800; text-align: center; }
      .rank { color: #70757a; font-size: 13px; font-weight: 800; }
      .rank.gold { color: #c48a00; }
      .rank.silver { color: #8a8f98; }
      .rank.bronze { color: #a7642a; }

      .health-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
      .health-grid { display: flex; flex-wrap: wrap; gap: 24px; }
      .status-indicator { min-width: 220px; display: flex; align-items: center; gap: 12px; }
      .status-dot { width: 10px; height: 10px; border-radius: 50%; background: #ea4335; }
      .status-dot.online { background: #34a853; animation: adminPulse 1.8s infinite; }
      .status-indicator strong { display: block; color: #202124; font-size: 14px; font-weight: 800; }
      .status-indicator p { color: #70757a; font-size: 13px; }
      @keyframes adminPulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(52,168,83,0.28); }
        50% { box-shadow: 0 0 0 8px rgba(52,168,83,0); }
      }

      .users-panel { }
      .users-toolbar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        flex-wrap: wrap;
      }
      .users-search-input {
        flex: 1;
        min-width: 200px;
        max-width: 340px;
        height: 38px;
        padding: 0 14px;
        border: 1px solid #dadce0;
        border-radius: 8px;
        font-size: 13px;
        font-family: Nunito, sans-serif;
      }
      .users-filter-group { display: flex; gap: 6px; flex-wrap: wrap; }
      .filter-chip {
        height: 32px;
        padding: 0 12px;
        border: 1px solid #dadce0;
        border-radius: 999px;
        background: #fff;
        color: #5f6368;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        font-family: Nunito, sans-serif;
        transition: all 160ms ease;
      }
      .filter-chip.active { background: #4285f4; color: #fff; border-color: #4285f4; }
      .filter-chip:hover:not(.active) { background: #f1f3f4; }

      .action-button {
        height: 28px;
        padding: 0 10px;
        border: 0;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 800;
        cursor: pointer;
        font-family: Nunito, sans-serif;
        white-space: nowrap;
      }
      .action-button.ban { background: #fce8e6; color: #c5221f; }
      .action-button.ban:hover { background: #EA4335; color: #fff; }
      .action-button.unban { background: #e6f4ea; color: #137333; }
      .action-button.unban:hover { background: #34A853; color: #fff; }
      .action-button:disabled { opacity: 0.5; cursor: default; }

      @media (max-width: 900px) {
        .admin-navbar { height: auto; min-height: 78px; flex-wrap: wrap; padding: 12px 16px; }
        .admin-nav-right { flex-wrap: wrap; justify-content: flex-end; }
        .admin-main { width: min(100% - 32px, 1280px); padding-top: 118px; }
        .stats-grid, .admin-two-column { grid-template-columns: 1fr 1fr; }
      }
      @media (max-width: 600px) {
        .stats-grid { grid-template-columns: 1fr 1fr; }
        .admin-two-column { grid-template-columns: 1fr; }
      }
      @media (min-width: 901px) and (max-width: 1200px) {
        .stats-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      }

      /* ── Dark mode ── */
      [data-theme="dark"] .admin-login-page,
      [data-theme="dark"] .admin-shell { background: #16171a; color: #e8eaed; }
      [data-theme="dark"] .admin-login-card,
      [data-theme="dark"] .admin-navbar,
      [data-theme="dark"] .admin-panel,
      [data-theme="dark"] .metric-card,
      [data-theme="dark"] .popular-item { background: #232428; border-color: #34363b; }
      [data-theme="dark"] .admin-navbar { border-bottom-color: #34363b; }
      [data-theme="dark"] .admin-login-card p,
      [data-theme="dark"] .metric-card strong,
      [data-theme="dark"] .panel-heading h2,
      [data-theme="dark"] .popular-item strong,
      [data-theme="dark"] table td { color: #e8eaed; }
      [data-theme="dark"] .metric-card span,
      [data-theme="dark"] .panel-heading p,
      [data-theme="dark"] .admin-nav-right > div > div,
      [data-theme="dark"] .empty-state,
      [data-theme="dark"] table th { color: #9aa0a6; }
      [data-theme="dark"] .metric-icon { background: #2d2f34; }
      [data-theme="dark"] .table-wrap thead th,
      [data-theme="dark"] .popular-item em { background: #1e1f22; color: #9aa0a6; }
      [data-theme="dark"] table tbody tr { border-top-color: #2d2f34; }
      [data-theme="dark"] table tbody tr:hover { background: #1e1f22; }
      [data-theme="dark"] .admin-login-card input,
      [data-theme="dark"] .recent-panel input,
      [data-theme="dark"] .skeleton { background: #1e1f22; border-color: #34363b; color: #e8eaed; }
      [data-theme="dark"] .skeleton { background: #2d2f34; }
      [data-theme="dark"] .status-indicator strong { color: #e8eaed; }
    `}</style>
  );
}

export default AdminDashboard;
